import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Table,
  Button,
  Typography,
  Divider,
  Tag,
  Select,
  DatePicker,
} from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import moment from 'moment'
import {
  getArenaGames,
  searchTournamentReport,
  exportTournamentReport,
} from 'src/api/arena'
import PrivateImage from 'src/components/PrivateImage'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

const GAME_MODE_OPTIONS = [
  { label: 'All Game Modes', value: '' },
  { label: 'Battle Royale', value: 'BATTLE_ROYALE' },
  { label: 'Daily Challenge', value: 'DAILY_CHALLENGE' },
]

const STATUS_OPTIONS = [
  { label: 'All Status', value: '' },
  { label: 'Complete', value: 'COMPLETE' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
]

const gameModeLabel = (mode?: string): string => {
  if (mode === 'BATTLE_ROYALE') return 'Battle Royale'
  if (mode === 'DAILY_CHALLENGE') return 'Daily Challenge'
  return mode ?? '-'
}

const durationLabel = (minutes?: number): string => {
  if (minutes === null || minutes === undefined) return '-'
  if (minutes < 60) return `${minutes} mins`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins ? `${hours}h ${mins}m` : `${hours} hours`
}

interface AppliedFilters {
  gameId: string
  gameMode: string
  state: string
  startDate?: string
  endDate?: string
}

interface GameMeta {
  title: string
  image?: string
}

const ArenaTournaments = (): JSX.Element => {
  const [rows, setRows] = useState<TournamentReportRow[]>([])
  const [total, setTotal] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [exporting, setExporting] = useState<boolean>(false)
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)

  const [games, setGames] = useState<ArenaGame[]>([])
  const [gameId, setGameId] = useState<string>('')
  const [gameMode, setGameMode] = useState<string>('')
  const [state, setState] = useState<string>('')
  const [range, setRange] = useState<
    [moment.Moment | null, moment.Moment | null] | null
  >(null)

  const [applied, setApplied] = useState<AppliedFilters>({
    gameId: '',
    gameMode: '',
    state: '',
  })

  useEffect(() => {
    const fetchGames = async (): Promise<void> => {
      const data = await getArenaGames()
      setGames(data)
    }
    fetchGames()
  }, [])

  const gamesById = useMemo(() => {
    const map: Record<string, GameMeta> = {}
    games.forEach((g) => {
      map[g.gameId] = {
        title: g.title ?? g.gameId,
        image: g.fullS3Path ?? g.thumbnailS3Path,
      }
    })
    return map
  }, [games])

  const gameOptions = useMemo(
    () => [
      { label: 'All Games', value: '' },
      ...games.map((g) => ({ label: g.title ?? g.gameId, value: g.gameId })),
    ],
    [games]
  )

  const fetchData = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const result = await searchTournamentReport({
        page: page - 1,
        pageSize,
        gameId: applied.gameId || undefined,
        gameMode: applied.gameMode || undefined,
        state: applied.state || undefined,
        startDate: applied.startDate,
        endDate: applied.endDate,
      })
      setRows(result.data)
      setTotal(result.total)
    } catch (error) {
      console.error('Error fetching tournaments report:', error)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, applied])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const currentFilters = (): AppliedFilters => ({
    gameId,
    gameMode,
    state,
    startDate: range?.[0] ? range[0].startOf('day').toISOString() : undefined,
    endDate: range?.[1] ? range[1].endOf('day').toISOString() : undefined,
  })

  const onSearch = (): void => {
    setApplied(currentFilters())
    setPage(1)
  }

  const onExport = async (format: 'pdf' | 'csv'): Promise<void> => {
    setExporting(true)
    try {
      await exportTournamentReport(format, currentFilters())
    } finally {
      setExporting(false)
    }
  }


  const columns: ColumnsType<TournamentReportRow> = [
    {
      title: 'Image',
      key: 'image',
      width: 80,
      render: (_: unknown, record: TournamentReportRow) => (
        <PrivateImage
          src={record.gameId ? gamesById[record.gameId]?.image : undefined}
          width={48}
          height={48}
        />
      ),
    },
    {
      title: 'Game Name',
      key: 'gameName',
      render: (_: unknown, record: TournamentReportRow) =>
        (record.gameId && gamesById[record.gameId]?.title) || record.gameId || '-',
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (value?: string) => value ?? '-',
    },
    {
      title: 'Game Mode',
      key: 'gameMode',
      render: (_: unknown, record: TournamentReportRow) =>
        gameModeLabel(record.gameMode),
    },
    {
      title: 'No. of Players',
      key: 'players',
      render: (_: unknown, record: TournamentReportRow) => record.players ?? '-',
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_: unknown, record: TournamentReportRow) =>
        durationLabel(record.durationMinutes),
    },
    {
      title: 'Entry Fee',
      dataIndex: 'entryFee',
      key: 'entryFee',
      render: (value?: string) => value ?? '-',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: TournamentReportRow) => (
        <Tag
          color={record.status === 'COMPLETE' ? '#2e7d32' : '#8a6d1f'}
          style={{ color: '#ffffff', border: 'none' }}
        >
          {record.status === 'COMPLETE' ? 'Complete' : 'In Progress'}
        </Tag>
      ),
    },
  ]

  return (
    <div>
      <Title level={3}>
        <Text>Tournaments</Text>
      </Title>
      <Divider style={{ margin: '10px 0' }} />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <Select
        className="search-input"
          style={{ width: 200 }}
          placeholder="Select Game"
          value={gameId}
          options={gameOptions}
          onChange={setGameId}
          showSearch
          optionFilterProp="label"
        />
        <Select
        className="search-input"
          style={{ width: 180 }}
          value={gameMode}
          options={GAME_MODE_OPTIONS}
          onChange={setGameMode}
        />
        <Select
        className='search-input'
          style={{ width: 150 }}
          value={state}
          options={STATUS_OPTIONS}
          onChange={setState}
        />
        <RangePicker
         style={{ width: 200 }}
          value={range}
          onChange={(values) =>
            setRange(values as [moment.Moment | null, moment.Moment | null])
          }
        />
        <Button type="primary" className="btn-primary" style={{ width: 100 }} onClick={onSearch}>
          Search
        </Button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Select
          placeholder="Export Report"
          style={{ width: 160 }}
          value={undefined}
          loading={exporting}
          disabled={exporting}
          onSelect={(value) => onExport(value as 'pdf' | 'csv')}
        >
          <Select.Option value="pdf">Export as PDF</Select.Option>
          <Select.Option value="csv">Export as CSV</Select.Option>
        </Select>
      </div>

      <Table<TournamentReportRow>
        rowKey={(record, index) => `${record.gameId}-${index}`}
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage)
            setPageSize(nextSize)
          },
        }}
      />
    </div>
  )
}

ArenaTournaments.displayName = 'ArenaTournaments'

export default ArenaTournaments
